// The single source of truth for what a "table + chairs" looks like in SVG.
// Shared by the reservations PlanView and the table-ordering live floor so
// the figure is identical everywhere and always matches the Konva editor in
// AdvancedTablesPage (same chair geometry, body shape, corner radius, fonts).
// Live overlays (state tint, totals, badges) layer ON TOP via `children`
// without forking the figure itself - the extension point the PlanView TODO
// described.
import type { FloorPlanElement } from '@restaurant/api-client';
import { circleChairs, rectChairs } from './tableChairs';

export const ADV_TABLE_FONT = 11;
export const BODY_FILL = '#e2e8f0';
export const BODY_STROKE = '#94a3b8';
export const CHAIR_FILL = '#cbd5e1';
export const TABLE_TEXT_FILL = '#1e293b';

export type TableElement = Extract<FloorPlanElement, { kind: 'table' }>;

export function SvgTableFigure({
  el,
  label,
  seats,
  /** Optional live-state accent: recolours the body outline only. The fill,
   *  chairs, shape, radius and label stay exactly as the editor draws them. */
  accentStroke,
  selected = false,
  onClick,
  children,
}: {
  el: TableElement;
  label: string;
  seats: number;
  accentStroke?: string;
  selected?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}) {
  const w = el.width;
  const h = el.height;
  const isCircle = el.shape === 'circle';
  const stroke = selected ? '#2563eb' : (accentStroke ?? BODY_STROKE);
  const strokeWidth = selected ? 2.5 : accentStroke ? 2 : 1.25;

  return (
    <g
      transform={`translate(${el.x}, ${el.y}) rotate(${el.rotation})`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {isCircle
        ? circleChairs(w, h, seats).map((c) => (
            <rect
              key={c.key}
              x={c.x - c.offsetX}
              y={c.y - c.offsetY}
              width={c.width}
              height={c.height}
              rx={2.5}
              fill={CHAIR_FILL}
              transform={`rotate(${c.rotation} ${c.x} ${c.y})`}
            />
          ))
        : rectChairs(w, h, seats).map((c) => (
            <rect
              key={c.key}
              x={c.x}
              y={c.y}
              width={c.width}
              height={c.height}
              rx={2.5}
              fill={CHAIR_FILL}
            />
          ))}
      {isCircle ? (
        <circle
          cx={w / 2}
          cy={h / 2}
          r={Math.min(w, h) / 2}
          fill={BODY_FILL}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      ) : (
        <rect
          width={w}
          height={h}
          rx={8}
          fill={BODY_FILL}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      )}
      <text
        x={w / 2}
        y={h / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={ADV_TABLE_FONT + 1}
        fontWeight={700}
        fill={TABLE_TEXT_FILL}
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {label}
      </text>
      {children}
    </g>
  );
}
